/**
 * Server-Sent Events (SSE) service for real-time cache invalidation notifications
 * Connects to backend SSE endpoint and dispatches events for UI updates
 */

class SseService {
  constructor() {
    this.eventSource = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.isConnected = false;
  }

  /**
   * Connect to SSE endpoint
   * @param {string} baseUrl - API base URL
   * @param {string} token - Authorization token
   * @param {string} customerId - Customer ID
   */
  connect(baseUrl, token, customerId) {
    if (this.eventSource) {
      this.disconnect();
    }

    const url = `${baseUrl}/automation/api/v1.0/Sse/events`;

    console.log('[SSE] Connecting to:', url);

    // Create EventSource with credentials
    // Note: EventSource doesn't support custom headers, so we use URL params for auth
    // The backend should accept token via query param for SSE connections
    this.eventSource = new EventSource(`${url}?token=${encodeURIComponent(token)}&customerId=${encodeURIComponent(customerId)}`, {
      withCredentials: true
    });

    this.eventSource.onopen = () => {
      console.log('[SSE] Connection opened');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.notifyListeners('connected', { timestamp: new Date() });
    };

    this.eventSource.onerror = (error) => {
      console.error('[SSE] Connection error:', error);
      this.isConnected = false;
      this.notifyListeners('error', { error });
      this.handleReconnect();
    };

    // Listen for cache invalidation events
    this.eventSource.addEventListener('cache-invalidation', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[SSE] Cache invalidation event:', data);
        this.notifyListeners('cache-invalidation', data);
      } catch (e) {
        console.error('[SSE] Failed to parse event data:', e);
      }
    });

    // Listen for connected event
    this.eventSource.addEventListener('connected', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[SSE] Connected event:', data);
      } catch (e) {
        console.error('[SSE] Failed to parse connected event:', e);
      }
    });

    // Listen for heartbeat
    this.eventSource.addEventListener('heartbeat', (event) => {
      console.log('[SSE] Heartbeat received');
    });
  }

  /**
   * Handle reconnection with exponential backoff
   */
  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[SSE] Max reconnect attempts reached');
      this.notifyListeners('max-reconnect-reached', {});
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      if (!this.isConnected && this.eventSource) {
        // Reconnect logic - the component should call connect again
        this.notifyListeners('reconnecting', { attempt: this.reconnectAttempts });
      }
    }, delay);
  }

  /**
   * Disconnect from SSE endpoint
   */
  disconnect() {
    if (this.eventSource) {
      console.log('[SSE] Disconnecting');
      this.eventSource.close();
      this.eventSource = null;
      this.isConnected = false;
    }
  }

  /**
   * Add event listener
   * @param {string} eventType - Event type to listen for
   * @param {function} callback - Callback function
   * @returns {function} Unsubscribe function
   */
  subscribe(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType).add(callback);

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(eventType);
      if (listeners) {
        listeners.delete(callback);
      }
    };
  }

  /**
   * Notify all listeners of an event
   * @param {string} eventType - Event type
   * @param {object} data - Event data
   */
  notifyListeners(eventType, data) {
    const listeners = this.listeners.get(eventType);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (e) {
          console.error('[SSE] Listener error:', e);
        }
      });
    }
  }

  /**
   * Check if connected
   * @returns {boolean}
   */
  isConnectedToServer() {
    return this.isConnected;
  }
}

// Export singleton instance
const sseService = new SseService();
export default sseService;
